import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { ref, onValue, update, remove } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import EngineerLayout from './EngineerLayout';
import { FaSearch, FaEye, FaCopy, FaFileExcel, FaPrint, FaFileAlt, FaUserCircle, FaTrashAlt, FaUpload } from 'react-icons/fa';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';

const EngineerEngineerList = () => {
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) navigate('/login');
        });

        const registrationsRef = ref(db, 'ekamwatapzp/EngineerRegistrations');
        const unsubData = onValue(registrationsRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const list = Object.keys(data).map(key => ({
                    id: key,
                    ...data[key]
                }));
                setRegistrations(list.reverse());
            } else {
                setRegistrations([]);
            }
            setLoading(false);
        });

        return () => {
            unsubscribe();
            unsubData();
        };
    }, [navigate]);

    const handleDelete = async (id) => {
        if (window.confirm("Do you want to delete this registration?")) {
            try {
                const engineerRef = ref(db, `ekamwatapzp/EngineerRegistrations/${id}`);
                await remove(engineerRef);
                toast.success("Successfully deleted!");
            } catch (error) {
                toast.error("Error: " + error.message);
            }
        }
    };

    const filteredData = registrations.filter(item => {
        const fullName = `${item.firstName} ${item.middleName} ${item.surname}`.toLowerCase();
        return fullName.includes(searchTerm.toLowerCase()) || 
               (item.technicalQualification && item.technicalQualification.toLowerCase().includes(searchTerm.toLowerCase()));
    });

    const downloadExcel = () => {
        const exportData = filteredData.map((item, index) => ({
            'अ.क्र.': index + 1,
            'नाव': `${item.firstName} ${item.middleName} ${item.surname}`,
            'व्हॉट्सॲप क्र.': item.whatsappNo || '',
            'ईमेल': item.email || '',
            'पात्रता': item.technicalQualification || '',
            'तालुका': item.taluka || '',
            'स्थिती': item.status || 'प्रलंबीत'
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "अभियंता यादी");
        XLSX.writeFile(wb, `अभियंता_यादी_${new Date().toLocaleDateString()}.xlsx`);
    };

    const copyToClipboard = () => {
        const headers = ["अ.क्र.", "अभियंत्याचे नाव", "व्हॉट्सॲप क्र.", "ईमेल", "पात्रता", "तालुका", "स्थिती"];
        const rows = filteredData.map((item, index) => [
            index + 1,
            `${item.firstName} ${item.middleName} ${item.surname}`,
            item.whatsappNo || '',
            item.email || '',
            item.technicalQualification || '',
            item.taluka || '',
            item.status || 'प्रलंबीत'
        ]);

        const text = [headers, ...rows].map(row => row.join('\t')).join('\n');
        navigator.clipboard.writeText(text).then(() => {
            toast.success("माहिती कॉपी झाली! (Copied to Clipboard)");
        }).catch(err => {
            toast.error("कॉपी करताना त्रुटी आली.");
        });
    };

    const handlePrint = () => {
        const printContent = `
            <html>
                <head>
                    <title>अभियंता यादी - सांगली जिल्हा परिषद</title>
                    <style>
                        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Marathi:wght@400;700&display=swap');
                        body { font-family: 'Noto Sans Marathi', sans-serif; padding: 40px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 30px; }
                        th, td { border: 1px solid #e2e8f0; padding: 12px 15px; text-align: left; font-size: 13px; }
                        th { background-color: #f8fafc; color: #475569; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; }
                        td { color: #1e293b; }
                        h2 { text-align: center; color: #0f172a; margin-bottom: 10px; font-size: 24px; }
                        p { text-align: center; color: #64748b; font-size: 14px; margin-top: 0; }
                        .header { border-bottom: 2px solid #085e53; padding-bottom: 20px; margin-bottom: 30px; }
                        .print-time { text-align: right; font-size: 11px; color: #94a3b8; margin-top: 30px; border-top: 1px solid #f1f5f9; padding-top: 10px; }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h2>सु.भे. अभियंता नोंदणी यादी</h2>
                        <p>सांगली जिल्हा परिषद (Zilla Parishad Sangli)</p>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th style="text-align: center; width: 50px;">अ.क्र.</th>
                                <th>अभियंत्याचे नाव</th>
                                <th>व्हॉट्सॲप क्र.</th>
                                <th>पात्रता</th>
                                <th>तालुका</th>
                                <th>स्थिती</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${filteredData.map((item, index) => `
                                <tr>
                                    <td style="text-align: center;">${index + 1}</td>
                                    <td style="font-weight: 700;">${item.firstName} ${item.middleName} ${item.surname}</td>
                                    <td>${item.whatsappNo}</td>
                                    <td>${item.technicalQualification || '-'}</td>
                                    <td>${item.taluka || '-'}</td>
                                    <td>${(item.status || 'Pending').toUpperCase()}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                    <div class="print-time">अहवाल दिनांक: ${new Date().toLocaleString()}</div>
                </body>
            </html>
        `;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.onload = () => {
            printWindow.print();
            printWindow.close();
        };
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50/50">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#085e53]"></div>
            </div>
        );
    }

    const StatusBadge = ({ status, type = 'status' }) => {
        const lowerStatus = (status || 'pending').toLowerCase();
        let colors = "bg-amber-50 text-amber-600 border-amber-100";
        let label = "प्रलंबीत (Pending)";

        if (lowerStatus === 'accepted' || lowerStatus === 'approved') {
            colors = "bg-emerald-50 text-emerald-600 border-emerald-100";
            label = "मंजूर (Approved)";
        } else if (lowerStatus === 'rejected') {
            colors = "bg-rose-50 text-rose-600 border-rose-100";
            label = "नामंजूर (Rejected)";
        }

        return (
            <span className={`px-2 py-1 rounded-md border text-[10px] font-black uppercase tracking-tight ${colors}`}>
                {label}
            </span>
        );
    };

    return (
        <EngineerLayout>
            <div className="p-6 md:p-8 bg-gray-50/30 min-h-screen">
                {/* Dark Header Card */}
                <div className="bg-[#1a1c23] rounded-3xl p-10 mb-8 shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl transition-transform duration-1000 group-hover:scale-110"></div>
                    
                    <div className="relative flex flex-col md:flex-row justify-between items-center gap-6">
                        <div>
                            <h1 className="text-4xl font-bold text-white tracking-tight mb-3">सु.भे. अभियंता यादी</h1>
                            <p className="text-gray-400 font-bold text-sm tracking-widest uppercase">
                                सर्व नवीन अभियंता नोंदणी अर्जांची यादी
                            </p>
                        </div>
                        <button 
                            onClick={() => navigate('/engineer/all-users')}
                            className="bg-[#2d2f39] hover:bg-[#383a45] text-white px-8 py-3 rounded-xl text-sm font-bold tracking-widest transition-all hover:scale-105 active:scale-95 shadow-lg border border-white/5"
                        >
                            मागे जा
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                    {/* Toolbar */}
                    <div className="p-6 border-b border-gray-50 flex flex-col lg:flex-row justify-between items-center gap-6 bg-white">
                        <div className="flex items-center gap-3">
                            <button onClick={copyToClipboard} className="flex items-center gap-2 px-6 py-2.5 bg-gray-800 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-black transition-all shadow-md active:scale-95">
                                <FaCopy /> Copy
                            </button>
                            <button onClick={downloadExcel} className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-emerald-700 transition-all shadow-md active:scale-95">
                                <FaFileExcel /> Excel
                            </button>
                            <button onClick={handlePrint} className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-blue-700 transition-all shadow-md active:scale-95">
                                <FaPrint /> Print
                            </button>
                        </div>

                        <div className="flex items-center gap-4 w-full lg:w-auto">
                            <div className="relative flex-grow">
                                <input 
                                    type="text" 
                                    placeholder="नाव किंवा पात्रतेनुसार शोधा..." 
                                    className="w-full lg:w-80 pl-12 pr-4 py-3 bg-gray-50 border border-gray-100 rounded-xl text-sm font-bold focus:bg-white focus:border-blue-500 transition-all outline-none text-gray-700"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50/50 border-b border-gray-100">
                                    <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] text-center w-16">#</th>
                                    <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">अभियंत्याचे नाव</th>
                                    <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] text-center">व्हॉट्सॲप क्र.</th>
                                    <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] text-center">पात्रता</th>
                                    <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] text-center">अ‍ॅडमिन</th>
                                    <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] text-center">अभियंता</th>
                                    <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] text-center">इन्चार्ज</th>
                                    <th className="px-6 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] text-center">कृती</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {filteredData.map((item, index) => (
                                    <tr key={item.id} className="group hover:bg-blue-50/30 transition-colors">
                                        <td className="px-6 py-6 text-center text-xs font-bold text-gray-400 italic">
                                            {String(index + 1).padStart(2, '0')}
                                        </td>
                                        <td className="px-6 py-6 border-r border-transparent group-hover:border-gray-100 transition-all">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-bold text-xs shadow-inner uppercase">
                                                    {item.firstName?.[0]}{item.surname?.[0]}
                                                </div>
                                                <div>
                                                    <div className="font-bold text-gray-800 text-sm group-hover:text-blue-700 transition">
                                                        {item.firstName} {item.middleName} {item.surname}
                                                    </div>
                                                    <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">
                                                        ID: {item.id.slice(-6).toUpperCase()}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-6 text-center">
                                            <span className="text-xs font-bold text-gray-600 tracking-wider font-mono bg-gray-100 px-3 py-1 rounded-full uppercase">
                                                {item.whatsappNo}
                                            </span>
                                        </td>
                                        <td className="px-6 py-6 text-center">
                                            <span className="text-xs font-bold text-gray-500 uppercase tracking-tighter">
                                                {item.technicalQualification || '-'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-6 text-center">
                                            <StatusBadge status={item.adminAccept ? 'accepted' : (item.status === 'rejected' ? 'rejected' : 'pending')} />
                                        </td>
                                        <td className="px-6 py-6 text-center">
                                            <StatusBadge status={item.status} />
                                        </td>
                                        <td className="px-6 py-6 text-center">
                                            <StatusBadge status={item.inchargeAccept ? 'accepted' : (item.status === 'rejected' ? 'rejected' : 'pending')} />
                                        </td>
                                        <td className="px-6 py-6">
                                            <div className="flex items-center gap-2 justify-center">
                                                <button 
                                                    onClick={() => navigate(`/engineer/engineers/details/${item.id}`)}
                                                    className="p-2.5 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-600 hover:text-white transition-all shadow-sm active:scale-95"
                                                    title="पहा"
                                                >
                                                    <FaEye size={14} />
                                                </button>
                                                <button 
                                                    onClick={() => navigate(`/engineer/engineers/documents/${item.id}`)}
                                                    className="p-2.5 bg-gray-50 text-gray-600 rounded-xl hover:bg-gray-800 hover:text-white transition-all shadow-sm active:scale-95"
                                                    title="दस्तऐवज"
                                                >
                                                    <FaUserCircle size={14} />
                                                </button>
                                                <button 
                                                    onClick={() => handleDelete(item.id)}
                                                    className="p-2.5 bg-rose-50 text-rose-600 rounded-xl hover:bg-rose-600 hover:text-white transition-all shadow-sm active:scale-95"
                                                    title="डिलीट"
                                                >
                                                    <FaTrashAlt size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {filteredData.length === 0 && (
                            <div className="py-24 text-center">
                                <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gray-50 mb-6">
                                    <FaSearch size={32} className="text-gray-200" />
                                </div>
                                <h3 className="text-xl font-bold text-gray-300 uppercase tracking-[0.2em]">माहिती उपलब्ध नाही</h3>
                                <p className="text-gray-400 font-bold mt-2">तुमच्या शोधानुसार कोणतेही अर्ज सापडले नाहीत.</p>
                            </div>
                        )}
                    </div>

                    <div className="p-6 bg-gray-50/50 border-t border-gray-100 flex justify-between items-center text-[10px] font-bold text-gray-400 uppercase tracking-widest px-10">
                        <span>एकूण अर्ज: {filteredData.length}</span>
                        <span>Zilla Parishad Sangli • {new Date().getFullYear()}</span>
                    </div>
                </div>
            </div>
        </EngineerLayout>
    );
};

export default EngineerEngineerList;
